import type { ApiError } from '../api/client';
import type { DeliveryMode, DriverInput, DriverPatch, DriverView } from '../api/endpoints';
import { formatPhoneInput, isPhoneComplete, phoneToE164 } from './phone';

/**
 * Форма курьера — заведение и правка: состояние, проверка и тело запроса.
 *
 * Чистые функции отдельно от панели — по той же причине, что и маска телефона: ошибки
 * формы ловятся тестом, а не глазами на приёмке. Правила повторяют `DriverInputDto`
 * сервера и схему `DriverInput` контракта; клиентская проверка — подсказка человеку,
 * а не защита: сервер проверяет то же самое ещё раз.
 *
 * Пределы длины — ограничения схемы контракта, а не эксплуатационные параметры
 * (FR-417 их не касается): поменять их можно только вместе с контрактом.
 */

export const FULL_NAME_MAX = 200;
export const EXTERNAL_CODE_MAX = 64;

export const EMPLOYMENT_KINDS = ['staff', 'contracted'] as const;
export type Employment = (typeof EMPLOYMENT_KINDS)[number];

/**
 * Языки, которые предлагает форма. Контракт допускает и `en`, но у приложения курьера
 * две локали (FR-557): английский выбрать можно было бы, а увидеть — нет.
 */
export const DRIVER_LANGUAGES = ['uz', 'ru'] as const;
export type DriverLanguage = (typeof DRIVER_LANGUAGES)[number];

export interface DriverFormState {
  readonly full_name: string;
  /** Как в поле: `+998 93 380 71 77`. На сервер уходит без пробелов. */
  readonly phone: string;
  readonly employment: Employment | '';
  readonly external_code: string;
  readonly language: DriverLanguage;
  readonly modes: readonly DeliveryMode[];
  readonly is_active: boolean;
}

export type DriverField =
  'full_name' | 'phone' | 'employment' | 'external_code' | 'modes' | 'is_active';

/** Код ошибки поля — ключ в `messages.cabinet.drivers.errors`. */
export type DriverFieldError =
  | 'fullNameRequired'
  | 'fullNameTooLong'
  | 'phoneInvalid'
  | 'externalCodeTooLong'
  | 'modesRequired'
  | 'phoneTaken'
  | 'externalCodeTaken'
  | 'departmentForbidden'
  | 'hasOpenShift';

export type DriverFormErrors = Partial<Record<DriverField, DriverFieldError>>;

/**
 * Пустая форма. Отдел у пользователя один — он и отмечен: выбирать не из чего, а
 * пустой список сервер отклонил бы. Отделов несколько — не отмечен ни один: курьер в
 * чужом по ошибке отделе получит чужие заказы, пусть человек выберет сам.
 */
export function emptyDriverForm(departments: readonly DeliveryMode[]): DriverFormState {
  return {
    full_name: '',
    phone: formatPhoneInput(''),
    employment: '',
    external_code: '',
    language: 'uz',
    modes: departments.length === 1 ? [...departments] : [],
    is_active: true,
  };
}

/**
 * Форма правки, заполненная курьером из списка.
 *
 * Язык в правке не участвует: `PATCH /web/drivers/{id}` его не принимает — курьер
 * меняет язык сам в приложении (FR-557).
 */
export function driverFormFrom(driver: DriverView): DriverFormState {
  return {
    full_name: driver.full_name,
    phone: formatPhoneInput(driver.phone ?? ''),
    employment: driver.employment ?? '',
    external_code: driver.external_code ?? '',
    language: driver.language === 'ru' ? 'ru' : 'uz',
    modes: [...(driver.modes ?? [])],
    is_active: driver.is_active,
  };
}

export function validateDriverForm(form: DriverFormState): DriverFormErrors {
  const errors: DriverFormErrors = {};
  const name = form.full_name.trim();

  if (name === '') {
    errors.full_name = 'fullNameRequired';
  } else if (name.length > FULL_NAME_MAX) {
    errors.full_name = 'fullNameTooLong';
  }

  if (!isPhoneComplete(form.phone)) {
    errors.phone = 'phoneInvalid';
  }

  if (form.external_code.trim().length > EXTERNAL_CODE_MAX) {
    errors.external_code = 'externalCodeTooLong';
  }

  if (form.modes.length === 0) {
    errors.modes = 'modesRequired';
  }

  return errors;
}

export function hasErrors(errors: DriverFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** Тело `POST /web/drivers`. Необязательное пустое поле не отправляется вовсе. */
export function toDriverInput(form: DriverFormState): DriverInput {
  const code = form.external_code.trim();

  return {
    full_name: form.full_name.trim(),
    phone: phoneToE164(form.phone),
    ...(form.employment === '' ? {} : { employment: form.employment }),
    ...(code === '' ? {} : { external_code: code }),
    language: form.language,
    is_active: form.is_active,
    modes: [...form.modes],
  };
}

/**
 * Тело `PATCH /web/drivers/{id}`: только то, что человек изменил.
 *
 * Отправлять форму целиком нельзя по двум причинам: правка одного ФИО не должна
 * переписывать отделы, которые параллельно поменял другой администратор, а в аудите
 * (FR-521) должно остаться именно изменённое поле. Пустой внешний код уходит `null` —
 * это снятие кода, а не «не трогать».
 */
export function toDriverPatch(form: DriverFormState, original: DriverFormState): DriverPatch {
  const next = toDriverInput(form);
  const before = toDriverInput(original);
  const result: { -readonly [K in keyof DriverPatch]: DriverPatch[K] } = {};

  if (next.full_name !== before.full_name) {
    result.full_name = next.full_name;
  }

  if (next.phone !== before.phone) {
    result.phone = next.phone;
  }

  if ((next.external_code ?? null) !== (before.external_code ?? null)) {
    result.external_code = next.external_code ?? null;
  }

  if ((next.employment ?? null) !== (before.employment ?? null)) {
    result.employment = next.employment ?? null;
  }

  if (next.is_active !== before.is_active) {
    result.is_active = next.is_active ?? true;
  }

  if (!sameSet(next.modes ?? [], before.modes ?? [])) {
    result.modes = [...(next.modes ?? [])];
  }

  return result;
}

function sameSet<T>(left: readonly T[], right: readonly T[]): boolean {
  return left.length === right.length && left.every((item) => right.includes(item));
}

/** Поля, которые сервер называет в `errors[].field` и которые есть в форме. */
const SERVER_FIELDS: Readonly<Record<string, DriverField>> = {
  full_name: 'full_name',
  phone: 'phone',
  employment: 'employment',
  external_code: 'external_code',
  modes: 'modes',
  is_active: 'is_active',
};

/** Чем подсветить поле, если сервер указал его без собственного кода причины. */
const FIELD_FALLBACK: Readonly<Record<DriverField, DriverFieldError | undefined>> = {
  full_name: 'fullNameRequired',
  phone: 'phoneInvalid',
  employment: undefined,
  external_code: 'externalCodeTooLong',
  modes: 'modesRequired',
  is_active: undefined,
};

/**
 * Отказ сервера → ошибки по полям.
 *
 * Код `code` не зависит от языка (разд. 3.3), поэтому сопоставление идёт по нему, а
 * подпись берётся из ресурсов кабинета. Остаток — `general`: его форма показывает
 * общим блоком с кодом для поддержки.
 */
export function errorsFromProblem(error: ApiError): {
  readonly fields: DriverFormErrors;
  readonly general: boolean;
} {
  switch (error.code) {
    case 'phone_taken':
      return { fields: { phone: 'phoneTaken' }, general: false };
    case 'external_code_taken':
    case 'external_code_taken_driver':
      return { fields: { external_code: 'externalCodeTaken' }, general: false };
    case 'department_forbidden':
      return { fields: { modes: 'departmentForbidden' }, general: false };
    // Отключить курьера на открытой смене нельзя: заказы на руках остались бы без
    // исполнителя. Ошибка — у переключателя «Активен», а не общим блоком.
    case 'driver_has_open_shift':
      return { fields: { is_active: 'hasOpenShift' }, general: false };
    default:
      break;
  }

  const fields: DriverFormErrors = {};

  for (const item of error.problem.errors ?? []) {
    const field = SERVER_FIELDS[item.field.split('.')[0] ?? ''];
    const code = field === undefined ? undefined : FIELD_FALLBACK[field];

    if (field !== undefined && code !== undefined) {
      fields[field] = code;
    }
  }

  return { fields, general: true };
}
